import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AppState, Text, TouchableOpacity, View, StyleSheet } from 'react-native';
import * as Crypto from 'expo-crypto';
import { useTheme } from '../../styles/ThemeProvider';
import {
  CONDITION_VALUES,
  confirmItemCondition,
  currentSpecificValue,
  getItemSpecificValuation,
  type ItemSpecificResponse,
  type ConfirmableCondition,
} from '../../lib/itemSpecificDelivery';
import type { UserProductWithDetails } from '../../types/newDatabaseTypes';

/** Detail-only, ephemeral contract. Never reads a stored or catalogue numeric fallback. */
export function ItemSpecificValuation({
  item,
  token,
  focused,
  onConfirmed,
}: {
  item: UserProductWithDetails;
  token: string | null;
  focused: boolean;
  onConfirmed: () => void;
}) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        text: { ...theme.typography.xs, color: theme.colors.textSecondary },
        value: { ...theme.typography.valueSm, color: theme.colors.accent },
        button: { paddingVertical: theme.spacing.sm },
      }),
    [theme]
  );
  const [response, setResponse] = useState<ItemSpecificResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(AppState.currentState === 'active');
  const [refresh, setRefresh] = useState(0);
  const current = useRef(item);
  current.current = item;
  const sequence = useRef(0);
  const responseItem = useRef<UserProductWithDetails | null>(null);
  const responseToken = useRef<string | null>(null);
  const keys = useRef<Record<string, string>>({});

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      sequence.current += 1;
      setResponse(null);
      setActive(state === 'active');
    });
    return () => subscription.remove();
  }, []);

  useEffect(() => {
    let disposed = false;
    let expiry: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      const requestSequence = ++sequence.current;
      if (expiry) clearTimeout(expiry);
      setResponse(null); // clear BEFORE every revalidation; no last-good value
      if (!token || !focused || !active) return;
      try {
        const result = await getItemSpecificValuation(item.id, token);
        if (disposed || sequence.current !== requestSequence || current.current !== item) return;
        responseItem.current = item;
        responseToken.current = token;
        setResponse(result);
        if (expiry) clearTimeout(expiry);
        if (result.status === 'specific' && result.provenance) {
          expiry = setTimeout(
            () => setResponse(null),
            Math.max(0, Math.min(5000, Date.parse(result.provenance.expires_at) - Date.now() || 0))
          );
        }
      } catch {
        if (!disposed && sequence.current === requestSequence) setResponse(null);
      }
    };
    void load();
    const interval = setInterval(() => {
      void load();
    }, 4000);
    return () => {
      disposed = true;
      sequence.current += 1;
      clearInterval(interval);
      if (expiry) clearTimeout(expiry);
    };
  }, [item, token, focused, active, refresh]);

  const safeResponse =
    responseItem.current === item && responseToken.current === token && focused && active
      ? response
      : null;
  const value = currentSpecificValue(safeResponse, item);
  const needsCondition = safeResponse?.reason === 'condition_confirmation_required';
  const confirm = async (condition: ConfirmableCondition) => {
    if (!token || !safeResponse?.identity_attempt_id || safeResponse.condition_generation === null)
      return;
    const capturedItem = item;
    const slot = `${item.id}|${safeResponse.identity_attempt_id}|${safeResponse.condition_generation}|${condition}`;
    keys.current[slot] = keys.current[slot] ?? Crypto.randomUUID();
    sequence.current += 1;
    setResponse(null);
    setBusy(true);
    try {
      await confirmItemCondition(item.id, token, {
        condition,
        identity_attempt_id: safeResponse.identity_attempt_id,
        condition_generation: safeResponse.condition_generation,
        request_key: keys.current[slot],
      });
      if (current.current === capturedItem) {
        onConfirmed();
        setRefresh((n) => n + 1);
      }
    } catch {
      setResponse(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <View testID="item-specific-valuation">
      {value !== null ? (
        <>
          <Text style={styles.value}>{`USD ${value}`}</Text>
          <Text style={styles.text}>
            Used-market asking reference — not adjusted for condition grade
          </Text>
        </>
      ) : (
        <Text style={styles.text}>
          {needsCondition
            ? 'Confirm condition to check for a price'
            : safeResponse?.reason === 'condition_unsupported'
              ? 'No price for this condition'
              : 'Price unavailable'}
        </Text>
      )}
      {needsCondition &&
        CONDITION_VALUES.map((condition) => (
          <TouchableOpacity
            key={condition}
            style={styles.button}
            accessibilityRole="button"
            disabled={busy}
            onPress={() => {
              void confirm(condition);
            }}
          >
            <Text style={styles.text}>{`Confirm condition: ${condition}`}</Text>
          </TouchableOpacity>
        ))}
    </View>
  );
}
